import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PathwaysApi } from 'src/api';
import { useEligibility } from 'src/hooks/useEligibility';
import { useHasCorePathSessionTypes } from 'src/hooks/useHasCorePathSessionTypes';
import {
  buildNextCheckpointCardModel,
  NextCheckpointCardModel,
} from './buildNextCheckpointCardModel';
import { getDaysSinceLastPathSession } from './getDaysSinceLastPathSession';
import { mapPathwayToLearningPathData } from './mapPathwayToLearningPathData';
import { LearningPathData } from './types';

export type NextPathStepState = {
  isPathEnabled: boolean;
  isLoading: boolean;
  isError: boolean;
  model: NextCheckpointCardModel | null;
  pathData: LearningPathData | null;
  /** Whole days since the last completed step; null before the first completion. */
  daysSinceLastSession: number | null;
};

/**
 * Single source of truth for "where is the learner on their path, and what is next?".
 *
 * Home splits that answer across two sibling cards (status and next session) and the
 * overview needs it too, so the feature gate, the query and the mapping live here once
 * rather than being repeated per consumer.
 */
export const useNextPathStep = (languagePairId?: number): NextPathStepState => {
  const { isEligibleForPathways, isLoading: isEligibilityLoading } = useEligibility(languagePairId);
  const isPathEnabled = isEligibleForPathways;
  const coreSessionTypesOnly = useHasCorePathSessionTypes();

  const {
    data: pathway,
    isLoading: isPathwayLoading,
    isError,
  } = useQuery({
    queryKey: ['activePathway', languagePairId],
    queryFn: () => PathwaysApi.getActivePathway(languagePairId as number),
    enabled: !!languagePairId && isPathEnabled,
    retry: false,
  });

  const isLoading = isEligibilityLoading || (isPathEnabled && isPathwayLoading);

  return useMemo(() => {
    if (!pathway) {
      return {
        isPathEnabled,
        isLoading,
        isError,
        model: null,
        pathData: null,
        daysSinceLastSession: null,
      };
    }

    const pathData = mapPathwayToLearningPathData(pathway, { coreSessionTypesOnly });
    return {
      isPathEnabled,
      isLoading,
      isError,
      model: buildNextCheckpointCardModel(pathData),
      pathData,
      daysSinceLastSession: getDaysSinceLastPathSession(pathway, new Date()),
    };
  }, [pathway, isPathEnabled, isLoading, isError, coreSessionTypesOnly]);
};
